import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { webAlert, webConfirm } from "../webDialog";
import {
  adminContentApi,
  contentApi,
  CreateQuestionInput,
  Question,
  QuestionLayout,
  QuestionOption,
} from "../../api/content";
import { Icon } from "../components/Icon";
import { useAuthStore } from "../../state/authStore";
import {
  VideoEntity,
  videoEntitiesApi,
} from "../../api/videoEntities";
import {
  EntityImage,
  useVideoEntities,
} from "./WebEntityManager";
import {
  extractFrames,
  ExtractedFrame,
  loadVideoBlobUrl,
} from "./frameExtraction";

// ─────────────────────────────────────────────────────────────────────────
// WebQuestionEditor — modal that lets a parent add / edit / delete the
// interactive questions on one of their own videos. Opened from Studio's
// gen-card preview.
//
// Layout matches the design handoff:
//  • PROMPT textarea
//  • ANSWER CHOICES list — each row is a green-circle "correct" dot, a
//    little image-picker button, the text input, and a remove (×) button.
//    Below the active row, an inline picker drops down with two tabs:
//      - "Frames from video" — auto-extracted stills (canvas seek on the
//        MP4, fetched as a Blob URL so JWT-auth still works).
//      - "Upload" — file picker + label, calls the existing entity API.
//    Picking either path POSTs to /v1/videos/:videoId/entities and attaches
//    the resulting imageUrl to the option.
//  • SHOW AT slider (mm:ss/mm:ss readout)
//  • Cancel / Save question footer
//
// A "Pictures / Text" toggle switches answers between image-only (no text
// fields) and text. New questions default to Pictures. Every question saves
// with layout="row"; the old TV "corners" layout picker was removed.
// ─────────────────────────────────────────────────────────────────────────

type Props = {
  videoId: string;
  videoTitle?: string;
  // Hard limit derived from the video's duration (in seconds). Drives the
  // slider's max value; falls back to a 600s cap (10min) when missing.
  videoDurationSec?: number;
  // Auth'd MP4 stream URL (the Generation's `videoUrl`). Used by the
  // "Frames from video" tab — fetched as a Blob URL for canvas extraction.
  // When missing, the Frames tab shows a friendly "no video URL" hint and
  // the Upload tab takes over.
  videoUrl?: string;
  // True when the parent uploaded their own video (no Wan-generated stills to
  // pull). Disables the "Frames from video" picker tab — uploaded videos can
  // only attach images via Upload.
  isUploaded?: boolean;
  onClose: () => void;
  // Fires after each successful save so the caller can refresh its local
  // count (e.g. "3 questions" pill on a gen-card). Called with the latest
  // question array.
  onChange?: (questions: Question[]) => void;
};

// One option inside a draft. `imageUrl` is what gets persisted; `entityId`
// is a local hint we use so the editor can show the correct picker checkmark
// on re-open. Not sent to the backend.
type DraftOption = {
  label: string;
  iconKey?: string;
  imageUrl?: string;
  entityId?: string;
};

type Draft = {
  // Either a real Question.id (existing row) or null (unsaved new row).
  id: string | null;
  tSec: number; // raw seconds — the slider edits this directly
  prompt: string;
  options: DraftOption[];
  correct: number;
  layout: QuestionLayout;
  // Answer type. "pictures" hides the per-option text fields and saves the
  // options image-only (label stripped). UI-only — not persisted as its own
  // field; re-derived from the saved options when the editor re-opens.
  answerMode: "text" | "pictures";
  // Local UI state.
  saving?: boolean;
  error?: string;
};

const DEFAULT_DRAFT_TSEC = 5;

function makeDefaultDraft(): Draft {
  return {
    id: null,
    tSec: DEFAULT_DRAFT_TSEC,
    prompt: "",
    // Picture questions default to 4 answers so they fill the diamond layout
    // (top · right · bottom · left) the player renders for image-only options.
    options: [{ label: "" }, { label: "" }, { label: "" }, { label: "" }],
    correct: 0,
    layout: "row",
    answerMode: "pictures",
  };
}

function questionToDraft(q: Question): Draft {
  const options = (q.options ?? []).map<DraftOption>((o) =>
    typeof o === "string"
      ? { label: o }
      : {
          label: o.label,
          iconKey: o.iconKey,
          imageUrl: o.imageUrl,
          entityId: extractEntityId(o.imageUrl),
        },
  );
  // Re-derive the answer mode: a question whose options are all images with no
  // text labels was authored as a picture question, so re-open it that way.
  const anyImage = options.some((o) => !!o.imageUrl);
  const anyLabel = options.some((o) => o.label.trim().length > 0);
  return {
    id: q.id,
    tSec: q.tSec,
    prompt: q.prompt,
    options,
    correct: q.correct,
    layout: "row",
    answerMode: anyImage && !anyLabel ? "pictures" : "text",
  };
}

// VideoEntity image URLs look like /v1/videos/entities/<id>/image; rip the
// id back out so re-opening the editor shows the right thumbnail checked.
function extractEntityId(imageUrl: string | undefined): string | undefined {
  if (!imageUrl) return undefined;
  const m = /\/v1\/videos\/entities\/([^/]+)\/image$/.exec(imageUrl);
  return m?.[1];
}

function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function WebQuestionEditor({
  videoId,
  videoTitle,
  videoDurationSec,
  videoUrl,
  isUploaded,
  onClose,
  onChange,
}: Props) {
  const { accessToken } = useAuthStore();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Drafts include both rows being edited (id present) and a new in-progress
  // row (id null). The composer for a brand-new question is held in
  // `addDraft`; existing rows being edited live in `editing`.
  const [editing, setEditing] = useState<Record<string, Draft>>({});
  const [addDraft, setAddDraft] = useState<Draft | null>(null);

  // Per-video object library. The frame-extractor uploads picked frames
  // into here on the fly; `refresh` re-pulls after a successful upload so
  // the picker shows the new entity immediately.
  const { entities, refresh: refreshEntities } = useVideoEntities(videoId);

  // Capped duration the slider snaps to. When the caller doesn't pass one
  // (older Studio code paths), fall back to 600s so the input still has
  // SOME range.
  const sliderMax = useMemo(() => {
    return videoDurationSec && videoDurationSec > 0 ? videoDurationSec : 600;
  }, [videoDurationSec]);

  // Initial load — the public /v1/content/:id endpoint already returns the
  // video's questions in tSec order. Reusing it avoids an admin-only fetch
  // wrapper we'd otherwise have to add.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);
    contentApi
      .get(videoId, accessToken)
      .then((v) => {
        if (cancelled) return;
        const qs = (v.questions ?? []) as Question[];
        setQuestions(qs);
        setLoadError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(
          e instanceof Error ? e.message : "Couldn't load questions.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, videoId]);

  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.tSec - b.tSec),
    [questions],
  );

  useEffect(() => {
    onChange?.(questions);
  }, [questions, onChange]);

  const validate = (d: Draft): string | null => {
    if (!Number.isFinite(d.tSec) || d.tSec < 0) {
      return "When (seconds) must be a non-negative number.";
    }
    if (videoDurationSec && d.tSec > videoDurationSec) {
      return `That's past the end of the video (${fmtTime(videoDurationSec)}).`;
    }
    if (!d.prompt.trim()) return "Question text is required.";
    const isPictures = d.answerMode === "pictures";
    const filled = d.options.filter((o) =>
      isPictures ? !!o.imageUrl : o.label.trim() || o.imageUrl,
    );
    if (filled.length < 2) {
      return isPictures
        ? "Add a picture to at least 2 answers."
        : "At least 2 answer options are required.";
    }
    if (d.correct < 0 || d.correct >= d.options.length) {
      return "Pick which option is the correct one.";
    }
    const correctOpt = d.options[d.correct];
    if (isPictures) {
      if (!correctOpt || !correctOpt.imageUrl) {
        return "The correct answer needs a picture.";
      }
    } else if (!correctOpt || (!correctOpt.label.trim() && !correctOpt.imageUrl)) {
      return "The correct option can't be empty.";
    }
    return null;
  };

  const draftToPayload = (d: Draft): CreateQuestionInput => {
    const isPictures = d.answerMode === "pictures";
    const keep = (o: DraftOption) =>
      isPictures ? !!o.imageUrl : Boolean(o.label.trim() || o.imageUrl);
    const kept = d.options.filter(keep);
    // Remap `correct` onto the kept list — dropping empty options would
    // otherwise shift the index off the intended answer.
    const correctIdx = Math.max(0, kept.indexOf(d.options[d.correct]));
    return {
      tSec: Math.round(d.tSec),
      prompt: d.prompt.trim(),
      options: kept.map((o) => ({
        // Picture answers are text-free (the player shows the image, never a
        // caption) so any stray label is dropped in that mode.
        label: isPictures ? "" : o.label.trim(),
        ...(o.iconKey ? { iconKey: o.iconKey } : {}),
        ...(o.imageUrl ? { imageUrl: o.imageUrl } : {}),
      })) as QuestionOption[],
      correct: correctIdx,
      layout: d.layout,
    };
  };

  const handleAdd = useCallback(async () => {
    if (!addDraft || !accessToken) return;
    const err = validate(addDraft);
    if (err) {
      setAddDraft({ ...addDraft, error: err });
      return;
    }
    setAddDraft({ ...addDraft, saving: true, error: undefined });
    try {
      const created = await adminContentApi.createQuestion(
        videoId,
        draftToPayload(addDraft),
        accessToken,
      );
      setQuestions((prev) => [...prev, created]);
      setAddDraft(null);
    } catch (e) {
      setAddDraft((d) =>
        d
          ? {
              ...d,
              saving: false,
              error: e instanceof Error ? e.message : "Couldn't save.",
            }
          : d,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addDraft, accessToken, videoId]);

  const handleSaveEdit = useCallback(
    async (qid: string) => {
      const d = editing[qid];
      if (!d || !accessToken) return;
      const err = validate(d);
      if (err) {
        setEditing((prev) => ({ ...prev, [qid]: { ...d, error: err } }));
        return;
      }
      setEditing((prev) => ({
        ...prev,
        [qid]: { ...d, saving: true, error: undefined },
      }));
      try {
        const updated = await adminContentApi.updateQuestion(
          qid,
          draftToPayload(d),
          accessToken,
        );
        setQuestions((prev) => prev.map((q) => (q.id === qid ? updated : q)));
        setEditing((prev) => {
          const next = { ...prev };
          delete next[qid];
          return next;
        });
      } catch (e) {
        setEditing((prev) => ({
          ...prev,
          [qid]: {
            ...d,
            saving: false,
            error: e instanceof Error ? e.message : "Couldn't save.",
          },
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editing, accessToken],
  );

  const handleDelete = useCallback(
    async (qid: string) => {
      if (!accessToken) return;
      const ok = await webConfirm("Delete this question? This can't be undone.", { confirmLabel: "Confirm", destructive: true });
      if (!ok) return;
      try {
        await adminContentApi.deleteQuestion(qid, accessToken);
        setQuestions((prev) => prev.filter((q) => q.id !== qid));
        setEditing((prev) => {
          const next = { ...prev };
          delete next[qid];
          return next;
        });
      } catch (e) {
        webAlert(e instanceof Error ? e.message : "Couldn't delete.");
      }
    },
    [accessToken],
  );

  // Escape always closes. The click targets can be covered or scrolled out of
  // view depending on content height, so this is the one dismissal path that
  // can't be blocked by layout.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="ws-modal ws-modal-stacked" onClick={onClose}>
      <div
        className="ws-modal-card wq-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="ws-modal-close" onClick={onClose}>
          ×
        </button>
        <div className="wq-head">
          <div className="wq-eyebrow">QUESTIONS</div>
          <div className="wq-title">{videoTitle ?? "Custom cartoon"}</div>
          <div className="wq-sub">
            Pause at a moment, ask a question. Up to 4 answer options each.
            {videoDurationSec
              ? ` Video is ${fmtTime(videoDurationSec)} long.`
              : ""}
          </div>
        </div>

        {loading ? (
          <div className="wq-loading">Loading questions…</div>
        ) : loadError ? (
          <div className="wq-error-bar">{loadError}</div>
        ) : (
          <>
            <div className="wq-list">
              {sortedQuestions.length === 0 && !addDraft && (
                <div className="wq-empty">
                  No questions yet. Add the first one below.
                </div>
              )}
              {sortedQuestions.map((q) => {
                const draft = editing[q.id];
                if (!draft) {
                  return (
                    <QuestionRow
                      key={q.id}
                      q={q}
                      onEdit={() =>
                        setEditing((prev) => ({
                          ...prev,
                          [q.id]: questionToDraft(q),
                        }))
                      }
                      onDelete={() => handleDelete(q.id)}
                    />
                  );
                }
                return (
                  <DraftForm
                    key={q.id}
                    draft={draft}
                    onChange={(next) =>
                      setEditing((prev) => ({ ...prev, [q.id]: next }))
                    }
                    onCancel={() =>
                      setEditing((prev) => {
                        const cp = { ...prev };
                        delete cp[q.id];
                        return cp;
                      })
                    }
                    onSave={() => handleSaveEdit(q.id)}
                    saveLabel="Save changes"
                    videoId={videoId}
                    videoUrl={videoUrl}
                    isUploaded={isUploaded}
                    videoDurationSec={sliderMax}
                    entities={entities}
                    onEntityCreated={() => refreshEntities()}
                  />
                );
              })}
            </div>

            {addDraft ? (
              <DraftForm
                draft={addDraft}
                onChange={setAddDraft}
                onCancel={() => setAddDraft(null)}
                onSave={handleAdd}
                saveLabel="Save question"
                videoId={videoId}
                videoUrl={videoUrl}
                isUploaded={isUploaded}
                videoDurationSec={sliderMax}
                entities={entities}
                onEntityCreated={() => refreshEntities()}
              />
            ) : (
              <button
                className="btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 12,
                }}
                onClick={() => setAddDraft(makeDefaultDraft())}
              >
                <Icon name="plus" size={14} /> Add a question
              </button>
            )}
          </>
        )}
      </div>

      <WQStyles />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// QuestionRow — read-only summary. Click "Edit" to swap into a DraftForm.
// ─────────────────────────────────────────────────────────────────────────
function QuestionRow({
  q,
  onEdit,
  onDelete,
}: {
  q: Question;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const opts = (q.options ?? []).map((o) =>
    typeof o === "string" ? { label: o } : o,
  );
  return (
    <div className="wq-row">
      <div className="wq-row-time">{fmtTime(q.tSec)}</div>
      <div className="wq-row-body">
        <div className="wq-row-prompt">{q.prompt}</div>
        <div className="wq-row-opts">
          {opts.map((o, i) => {
            const entityId = extractEntityId(o.imageUrl);
            const ent = entityId
              ? ({
                  id: entityId,
                  imageUrl: o.imageUrl as string,
                  label: o.label,
                } as Pick<VideoEntity, "id" | "imageUrl" | "label">)
              : null;
            return (
              <span
                key={i}
                className={
                  "wq-opt-pill " + (i === q.correct ? "correct" : "")
                }
              >
                {ent && (
                  <span className="wq-opt-pill-img">
                    <EntityImage entity={ent} alt={o.label} />
                  </span>
                )}
                {o.label || <em style={{ opacity: 0.6 }}>image</em>}
              </span>
            );
          })}
        </div>
      </div>
      <div className="wq-row-actions">
        <button className="btn btn-ghost wq-mini" onClick={onEdit}>
          Edit
        </button>
        <button
          className="btn btn-ghost wq-mini wq-danger"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// DraftForm — the editable card matching the design handoff exactly.
// ─────────────────────────────────────────────────────────────────────────
function DraftForm({
  draft,
  onChange,
  onCancel,
  onSave,
  saveLabel,
  videoId,
  videoUrl,
  isUploaded,
  videoDurationSec,
  entities,
  onEntityCreated,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  saveLabel: string;
  videoId: string;
  videoUrl?: string;
  isUploaded?: boolean;
  videoDurationSec: number;
  entities: VideoEntity[];
  onEntityCreated: () => void;
}) {
  // Which option's inline picker is open (-1 = none).
  const [pickerOpenFor, setPickerOpenFor] = useState<number>(-1);

  const update = (patch: Partial<Draft>) =>
    onChange({ ...draft, ...patch, error: undefined });

  const setOption = (i: number, label: string) => {
    const opts = draft.options.slice();
    opts[i] = { ...opts[i], label };
    update({ options: opts });
  };
  const attachImageToOption = (i: number, ent: VideoEntity) => {
    const opts = draft.options.slice();
    const prev = opts[i] ?? { label: "" };
    // Don't autofill the text from the image's label — picture answers stay
    // text-free (the player shows the image, never a caption). Whatever the
    // parent typed (if anything) is preserved.
    opts[i] = {
      ...prev,
      imageUrl: ent.imageUrl,
      entityId: ent.id,
    };
    update({ options: opts });
  };
  const clearOptionImage = (i: number) => {
    const opts = draft.options.slice();
    opts[i] = { ...opts[i], imageUrl: undefined, entityId: undefined };
    update({ options: opts });
  };
  const addOption = () => {
    if (draft.options.length >= 4) return;
    update({ options: [...draft.options, { label: "" }] });
  };
  const removeOption = (i: number) => {
    if (draft.options.length <= 2) return;
    const opts = draft.options.slice();
    opts.splice(i, 1);
    let nextCorrect = draft.correct;
    if (i === draft.correct) nextCorrect = 0;
    else if (i < draft.correct) nextCorrect = draft.correct - 1;
    update({ options: opts, correct: nextCorrect });
    if (pickerOpenFor === i) setPickerOpenFor(-1);
    else if (pickerOpenFor > i) setPickerOpenFor(pickerOpenFor - 1);
  };

  return (
    <div className="wq-form">
      {/* Prompt */}
      <label className="wq-field">
        <span className="wq-field-label">Prompt</span>
        <textarea
          className="w-input wq-textarea"
          placeholder="e.g. How many cookies do you see?"
          value={draft.prompt}
          onChange={(e) => update({ prompt: e.target.value })}
          rows={2}
        />
      </label>

      {/* Answer choices */}
      <div className="wq-options">
        <div className="wq-answers-head">
          <div className="wq-field-label">
            {draft.answerMode === "pictures"
              ? "Answer pictures · tap the dot to mark the correct one"
              : "Answer choices · tap the dot to mark the correct one"}
          </div>
          <div
            className="wq-mode-toggle"
            role="tablist"
            aria-label="Answer type"
          >
            <button
              type="button"
              role="tab"
              aria-selected={draft.answerMode === "pictures"}
              className={
                "wq-mode-btn " +
                (draft.answerMode === "pictures" ? "active" : "")
              }
              onClick={() => update({ answerMode: "pictures" })}
            >
              Pictures
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={draft.answerMode === "text"}
              className={
                "wq-mode-btn " + (draft.answerMode === "text" ? "active" : "")
              }
              onClick={() => update({ answerMode: "text" })}
            >
              Text
            </button>
          </div>
        </div>
        {draft.answerMode === "pictures" && (
          <div className="wq-mode-hint">
            Picture answers — tap each slot to add an image. No text needed.
          </div>
        )}
        {draft.options.map((o, i) => {
          const matchedEntity = o.entityId
            ? entities.find((e) => e.id === o.entityId)
            : undefined;
          const isPickerOpen = pickerOpenFor === i;
          return (
            <React.Fragment key={i}>
              <div className="wq-option-row">
                <button
                  type="button"
                  className={
                    "wq-correct-dot " + (draft.correct === i ? "selected" : "")
                  }
                  onClick={() => update({ correct: i })}
                  aria-label={`Mark option ${i + 1} as correct`}
                >
                  {draft.correct === i && <CheckGlyph />}
                </button>
                {draft.answerMode === "pictures" ? (
                  <button
                    type="button"
                    className={
                      "wq-img-slot " + (o.imageUrl ? "filled" : "empty")
                    }
                    onClick={() => setPickerOpenFor(isPickerOpen ? -1 : i)}
                    title={
                      o.imageUrl
                        ? "Change picture"
                        : "Add a picture for this answer"
                    }
                  >
                    {o.imageUrl && matchedEntity ? (
                      <EntityImage
                        entity={matchedEntity}
                        alt={matchedEntity.label}
                      />
                    ) : (
                      <span className="wq-img-slot-empty">
                        <PictureGlyph />
                        <span>Add picture</span>
                      </span>
                    )}
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className={
                        "wq-img-btn " + (o.imageUrl ? "filled" : "empty")
                      }
                      onClick={() =>
                        setPickerOpenFor(isPickerOpen ? -1 : i)
                      }
                      title={
                        o.imageUrl
                          ? "Change image"
                          : "Attach an image to this answer"
                      }
                    >
                      {o.imageUrl && matchedEntity ? (
                        <EntityImage
                          entity={matchedEntity}
                          alt={o.label || matchedEntity.label}
                        />
                      ) : (
                        <PictureGlyph />
                      )}
                    </button>
                    <input
                      className="w-input wq-opt-input"
                      placeholder={`Option ${i + 1}`}
                      value={o.label}
                      onChange={(e) => setOption(i, e.target.value)}
                    />
                  </>
                )}
                <button
                  type="button"
                  className="wq-x-btn"
                  onClick={() => removeOption(i)}
                  disabled={draft.options.length <= 2}
                  title={
                    draft.options.length <= 2
                      ? "At least 2 options required"
                      : "Remove option"
                  }
                >
                  ×
                </button>
              </div>
              {isPickerOpen && (
                <InlineImagePicker
                  videoId={videoId}
                  videoUrl={videoUrl}
                  allowFrames={!isUploaded}
                  optionIndex={i}
                  currentImageUrl={o.imageUrl}
                  onClose={() => setPickerOpenFor(-1)}
                  onPick={(ent) => {
                    attachImageToOption(i, ent);
                    setPickerOpenFor(-1);
                  }}
                  onClear={
                    o.imageUrl
                      ? () => {
                          clearOptionImage(i);
                          setPickerOpenFor(-1);
                        }
                      : undefined
                  }
                  onEntityCreated={onEntityCreated}
                />
              )}
            </React.Fragment>
          );
        })}
        {draft.options.length < 4 && (
          <button
            type="button"
            className="wq-add-option"
            onClick={addOption}
          >
            <Icon name="plus" size={12} /> Add another option
          </button>
        )}
      </div>

      {/* Time slider */}
      <div className="wq-time">
        <div className="wq-field-label">
          Show at · {fmtTime(draft.tSec)} of {fmtTime(videoDurationSec)}
        </div>
        <div className="wq-time-row">
          <div className="wq-clock">
            <ClockGlyph />
          </div>
          <input
            type="range"
            className="wq-slider"
            min={0}
            max={Math.max(1, Math.round(videoDurationSec))}
            step={1}
            value={Math.min(Math.round(draft.tSec), Math.round(videoDurationSec))}
            onChange={(e) => update({ tSec: Number(e.target.value) })}
            aria-label="When to show the question"
          />
          <div className="wq-time-readout">{fmtTime(draft.tSec)}</div>
        </div>
      </div>

      {draft.error && <div className="wq-error-bar">{draft.error}</div>}

      <div className="wq-form-actions">
        <button
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={draft.saving}
        >
          Cancel
        </button>
        <button className="btn" onClick={onSave} disabled={draft.saving}>
          {draft.saving ? "Saving…" : saveLabel}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// InlineImagePicker — drops down below the option row with two tabs:
//   "Frames from video" : auto-extracted stills (canvas+seek on the MP4)
//   "Upload"            : file picker + label, calls the entity API
// Both paths create a VideoEntity on confirmation so the option's imageUrl
// is a stable, server-side reference (not a transient data URL).
// ─────────────────────────────────────────────────────────────────────────
type PickerTab = "frames" | "upload";

function InlineImagePicker({
  videoId,
  videoUrl,
  allowFrames = true,
  optionIndex,
  currentImageUrl,
  onClose,
  onPick,
  onClear,
  onEntityCreated,
}: {
  videoId: string;
  videoUrl?: string;
  // When false (parent-uploaded videos) the "Frames from video" tab is hidden
  // entirely — there are no Wan stills to auto-extract, so the only way to
  // attach an answer image is the Upload tab.
  allowFrames?: boolean;
  optionIndex: number;
  currentImageUrl?: string;
  onClose: () => void;
  onPick: (ent: VideoEntity) => void;
  onClear?: () => void;
  onEntityCreated: () => void;
}) {
  // Frames are only an option when allowed AND we actually have a stream URL.
  const framesUsable = allowFrames && !!videoUrl;
  // Default to the Frames tab when usable, otherwise jump straight to Upload —
  // no point showing an empty (or hidden) frames row.
  const [tab, setTab] = useState<PickerTab>(framesUsable ? "frames" : "upload");

  return (
    <div className="wq-picker">
      <div className="wq-picker-tabs">
        {allowFrames && (
          <button
            type="button"
            className={
              "wq-picker-tab " + (tab === "frames" ? "active" : "")
            }
            onClick={() => setTab("frames")}
            disabled={!videoUrl}
            title={!videoUrl ? "No video URL yet — try Upload" : undefined}
          >
            Frames from video
          </button>
        )}
        <button
          type="button"
          className={
            "wq-picker-tab " + (tab === "upload" ? "active" : "")
          }
          onClick={() => setTab("upload")}
        >
          Upload
        </button>
        <div style={{ flex: 1 }} />
        {onClear && (
          <button
            type="button"
            className="btn btn-ghost wq-mini"
            onClick={onClear}
            title="Remove image from this option"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          className="wq-picker-close"
          onClick={onClose}
          aria-label="Close picker"
        >
          ×
        </button>
      </div>
      {tab === "frames" ? (
        <FramesTab
          key={`${videoId}-${optionIndex}`}
          videoUrl={videoUrl}
          videoId={videoId}
          currentImageUrl={currentImageUrl}
          onPick={onPick}
          onEntityCreated={onEntityCreated}
        />
      ) : (
        <UploadTab
          videoId={videoId}
          onPick={onPick}
          onEntityCreated={onEntityCreated}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// FramesTab — pulls 6 evenly-spaced stills from the MP4 client-side and
// renders them as a horizontal row of tiles. Clicking one uploads it as a
// VideoEntity (so the imageUrl is server-hosted) and attaches to the option.
// ─────────────────────────────────────────────────────────────────────────
const FRAME_COUNT = 6;

function FramesTab({
  videoUrl,
  videoId,
  onPick,
  onEntityCreated,
}: {
  videoUrl?: string;
  videoId: string;
  currentImageUrl?: string;
  onPick: (ent: VideoEntity) => void;
  onEntityCreated: () => void;
}) {
  const { accessToken } = useAuthStore();
  const [frames, setFrames] = useState<ExtractedFrame[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // Per-frame "saving" indicator so the row dims the tile while we POST
  // to the entity-create endpoint.
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!videoUrl || !accessToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      let url: string | null = null;
      try {
        url = await loadVideoBlobUrl(videoUrl, accessToken);
        blobUrlRef.current = url;
        const out = await extractFrames(url, FRAME_COUNT);
        if (cancelled) return;
        setFrames(out);
      } catch (e) {
        if (cancelled) return;
        setError(
          e instanceof Error ? e.message : "Couldn't read video frames.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [videoUrl, accessToken]);

  const onPickFrame = async (idx: number) => {
    if (!accessToken) return;
    const frame = frames[idx];
    if (!frame) return;
    setSavingIndex(idx);
    try {
      const ent = await videoEntitiesApi.create(
        videoId,
        {
          label: `Frame at ${fmtTime(frame.tSec)}`,
          dataUrl: frame.dataUrl,
          tSec: frame.tSec,
        },
        accessToken,
      );
      onEntityCreated();
      onPick(ent);
    } catch (e) {
      webAlert(
        e instanceof Error ? e.message : "Couldn't save that frame.",
      );
    } finally {
      setSavingIndex(null);
    }
  };

  if (!videoUrl) {
    return (
      <div className="wq-frames-empty">
        This video doesn&apos;t have a stream URL yet — try the Upload tab.
      </div>
    );
  }

  return (
    <div className="wq-frames">
      <div className="wq-frames-hint">
        Auto-extracted stills from this video. Pick one for the answer.
      </div>
      {loading ? (
        <div className="wq-frames-row">
          {Array.from({ length: FRAME_COUNT }).map((_, i) => (
            <div key={i} className="wq-frame-tile loading" />
          ))}
        </div>
      ) : error ? (
        <div className="wq-error-bar">{error}</div>
      ) : (
        <div className="wq-frames-row">
          {frames.map((f, i) => (
            <button
              key={i}
              type="button"
              className={
                "wq-frame-tile " + (savingIndex === i ? "saving" : "")
              }
              onClick={() => onPickFrame(i)}
              disabled={savingIndex !== null}
            >
              <img
                src={f.dataUrl}
                alt=""
                onError={(e) => {
                  // If the decode failed (e.g. the upstream MP4 fetch was
                  // truncated or the browser couldn't seek), hide the broken
                  // <img> instead of letting the alt-text render as visible
                  // fallback. The timestamp overlay below still gives the
                  // user visual context for the tile.
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="wq-frame-t">{fmtTime(f.tSec)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// UploadTab — file picker + label. Identical flow to the standalone
// entity manager, just embedded in the picker.
// ─────────────────────────────────────────────────────────────────────────
function UploadTab({
  videoId,
  onPick,
  onEntityCreated,
}: {
  videoId: string;
  onPick: (ent: VideoEntity) => void;
  onEntityCreated: () => void;
}) {
  const { accessToken } = useAuthStore();
  const [label, setLabel] = useState("");
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileRef.current) fileRef.current.value = "";
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      if (!result.startsWith("data:image/")) {
        setError("Please pick a PNG, JPEG, or WebP image.");
        return;
      }
      setDataUrl(result);
      if (!label.trim()) {
        setLabel(file.name.replace(/\.[^.]+$/, ""));
      }
      setError(null);
    };
    reader.onerror = () => setError("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  const onSave = async () => {
    if (!accessToken) return;
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Give the object a name (e.g. "Moon").');
      return;
    }
    if (!dataUrl) {
      setError("Pick an image first.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const ent = await videoEntitiesApi.create(
        videoId,
        { label: trimmed, dataUrl },
        accessToken,
      );
      onEntityCreated();
      onPick(ent);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't upload that image.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wq-upload">
      <div className="wq-upload-row">
        <button
          type="button"
          className="wq-upload-preview"
          onClick={onPickFile}
          title="Pick an image from your computer"
        >
          {dataUrl ? (
            <img src={dataUrl} alt="" />
          ) : (
            <span className="wq-upload-empty">
              <Icon name="plus" size={20} />
              <span>Pick image</span>
            </span>
          )}
        </button>
        <div className="wq-upload-form">
          <input
            className="w-input"
            placeholder='Name (e.g. "Moon")'
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div className="wq-upload-actions">
            <button
              type="button"
              className="btn btn-ghost wq-mini"
              onClick={onPickFile}
            >
              {dataUrl ? "Replace image" : "Pick image"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={onSave}
              disabled={saving || !dataUrl || !label.trim()}
            >
              {saving ? "Uploading…" : "Use this image"}
            </button>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={onFileChange}
        />
      </div>
      {error && <div className="wq-error-bar">{error}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Small inline SVG glyphs — kept here so the editor is fully self-contained
// and matches the design without pulling more icons into the shared Icon set.
// ─────────────────────────────────────────────────────────────────────────
function CheckGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12.5l4.5 4.5L19 7.5"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PictureGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect
        x="3"
        y="5"
        width="18"
        height="14"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <circle cx="9" cy="11" r="1.6" fill="currentColor" />
      <path
        d="M21 17l-5-5-7 7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ClockGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Inline styles. Lives next to the component so the editor is self-contained.
function WQStyles() {
  return (
    <style>{`
      /* Compound selector to outrank the base .ws-modal-card width rule
         (which is 640px) — same trick as WebVideoDetailsEditor. */
      .ws-modal-card.wq-card {
        width: min(960px, 96vw);
        max-width: min(960px, 96vw);
        max-height: calc(100vh - 48px);
        overflow-y: auto;
        padding: 28px 32px 24px;
      }
      .wq-head { margin-bottom: 16px; }
      .wq-eyebrow {
        font-size: 11px; font-weight: 900; letter-spacing: 0.12em;
        text-transform: uppercase; color: var(--primary-ink);
      }
      .wq-title {
        font-family: var(--display);
        font-size: 24px; font-weight: 900;
        color: var(--ink); margin-top: 4px;
      }
      .wq-sub {
        font-size: 13px; font-weight: 600;
        color: var(--ink-2); margin-top: 4px;
      }
      .wq-loading {
        padding: 24px; text-align: center;
        color: var(--ink-3); font-weight: 700;
      }
      .wq-empty {
        padding: 22px; text-align: center;
        color: var(--ink-3); font-weight: 700;
        border: 1.5px dashed var(--line);
        border-radius: 12px;
      }
      .wq-list {
        display: flex; flex-direction: column; gap: 10px;
        margin-bottom: 14px;
      }
      .wq-row {
        display: flex; align-items: flex-start; gap: 12px;
        padding: 12px 14px;
        border: 1.5px solid var(--line);
        border-radius: 12px;
        background: var(--surface);
      }
      .wq-row-time {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 13px; font-weight: 800;
        color: var(--primary-ink);
        background: var(--bg-warm);
        padding: 4px 8px; border-radius: 6px;
        flex: 0 0 auto;
      }
      .wq-row-body { flex: 1; min-width: 0; }
      .wq-row-prompt {
        font-size: 14px; font-weight: 800; color: var(--ink);
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .wq-row-opts {
        margin-top: 6px;
        display: flex; flex-wrap: wrap; gap: 6px;
      }
      .wq-opt-pill {
        font-size: 11.5px; font-weight: 700;
        color: var(--ink-2);
        background: var(--bg-warm);
        padding: 3px 8px 3px 4px;
        border-radius: 999px;
        display: inline-flex; align-items: center; gap: 5px;
      }
      .wq-opt-pill.correct {
        color: #2A6B23;
        background: #E8F5E1;
      }
      .wq-opt-pill-img {
        width: 18px; height: 18px;
        border-radius: 50%;
        overflow: hidden;
        background: rgba(0,0,0,0.06);
        display: inline-grid; place-items: center;
        flex: 0 0 auto;
      }
      .wq-opt-pill-img img {
        width: 100%; height: 100%;
        object-fit: cover;
      }
      .wq-row-actions {
        display: flex; gap: 6px; flex: 0 0 auto;
      }
      .wq-mini {
        padding: 6px 10px;
        font-size: 12px;
      }
      .wq-danger {
        color: #C44;
      }

      /* ── Draft form (the new dashed-border card) ─────────────────────── */
      .wq-form {
        border: 2.5px dashed var(--primary);
        background: var(--bg-warm);
        border-radius: 18px;
        padding: 22px;
        display: flex; flex-direction: column; gap: 16px;
      }
      .wq-field { display: flex; flex-direction: column; gap: 6px; }
      .wq-field-label {
        font-size: 11px; font-weight: 800;
        letter-spacing: 0.10em; text-transform: uppercase;
        color: var(--ink-3);
      }
      .wq-textarea {
        resize: vertical;
        min-height: 64px;
      }

      .wq-options { display: flex; flex-direction: column; gap: 10px; }
      .wq-option-row {
        display: flex; align-items: center; gap: 10px;
      }
      .wq-correct-dot {
        width: 26px; height: 26px;
        border-radius: 50%;
        background: var(--surface);
        border: 2px solid var(--line-strong);
        cursor: pointer;
        flex: 0 0 auto;
        display: grid; place-items: center;
        transition: background-color 120ms ease, border-color 120ms ease;
        padding: 0;
      }
      .wq-correct-dot:hover {
        border-color: var(--primary);
      }
      .wq-correct-dot.selected {
        background: #6FBF5B;
        border-color: #6FBF5B;
      }
      .wq-img-btn {
        width: 36px; height: 36px;
        border-radius: 8px;
        flex: 0 0 auto;
        cursor: pointer;
        padding: 0;
        overflow: hidden;
        display: grid; place-items: center;
        background: var(--surface);
      }
      .wq-img-btn.empty {
        border: 1.5px dashed var(--line-strong);
        color: var(--ink-3);
      }
      .wq-img-btn.empty:hover {
        border-color: var(--primary);
        color: var(--primary-ink);
      }
      .wq-img-btn.filled {
        border: 1.5px solid var(--primary);
      }
      .wq-img-btn img {
        width: 100%; height: 100%; object-fit: cover;
      }
      .wq-opt-input { flex: 1; min-width: 0; }
      .wq-icon-input { max-width: 120px; }
      .wq-x-btn {
        width: 32px; height: 32px;
        border-radius: 8px;
        flex: 0 0 auto;
        border: 1.5px solid var(--line);
        background: var(--surface);
        color: var(--ink-2);
        cursor: pointer;
        font: inherit; font-weight: 900; font-size: 16px;
        line-height: 1;
      }
      .wq-x-btn:hover:not(:disabled) {
        border-color: #C44; color: #C44;
      }
      .wq-x-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .wq-add-option {
        align-self: flex-start;
        display: inline-flex; align-items: center; gap: 6px;
        background: transparent; border: 0;
        color: var(--primary-ink);
        font: inherit; font-weight: 800; font-size: 13px;
        cursor: pointer;
        padding: 6px 4px;
      }
      .wq-add-option:hover { text-decoration: underline; }

      /* ── Answer-type toggle (Text vs Pictures) ───────────────────────── */
      .wq-answers-head {
        display: flex; align-items: center; justify-content: space-between;
        gap: 12px;
      }
      .wq-mode-toggle {
        display: inline-flex; flex: 0 0 auto;
        background: var(--surface);
        border: 1.5px solid var(--line);
        border-radius: 999px; padding: 2px;
      }
      .wq-mode-btn {
        border: 0; background: transparent; cursor: pointer;
        font: inherit; font-weight: 800; font-size: 12px;
        color: var(--ink-3); padding: 5px 14px; border-radius: 999px;
        transition: background-color 120ms ease, color 120ms ease;
      }
      .wq-mode-btn:hover:not(.active) { color: var(--ink); }
      .wq-mode-btn.active { background: var(--primary); color: #fff; }
      .wq-mode-hint {
        font-size: 12px; font-weight: 600; color: var(--ink-2);
        margin-top: -2px;
      }

      /* Wide image slot used in Pictures mode (replaces the text field). */
      .wq-img-slot {
        flex: 1; min-width: 0; height: 56px;
        border-radius: 10px; cursor: pointer; padding: 0;
        overflow: hidden;
        display: grid; place-items: center;
        background: var(--surface);
        transition: border-color 120ms ease, color 120ms ease;
      }
      .wq-img-slot.empty {
        border: 1.5px dashed var(--line-strong);
        color: var(--ink-3);
      }
      .wq-img-slot.empty:hover {
        border-color: var(--primary); color: var(--primary-ink);
      }
      .wq-img-slot.filled { border: 1.5px solid var(--primary); }
      .wq-img-slot img { width: 100%; height: 100%; object-fit: cover; }
      .wq-img-slot-empty {
        display: inline-flex; align-items: center; gap: 8px;
        font-size: 13px; font-weight: 800;
      }

      /* ── Inline picker ───────────────────────────────────────────────── */
      .wq-picker {
        background: var(--surface);
        border: 1.5px solid var(--line);
        border-radius: 14px;
        padding: 12px 14px 14px;
        margin-top: -2px;
        margin-left: 46px; /* aligns roughly under the image button */
      }
      .wq-picker-tabs {
        display: flex; align-items: center; gap: 6px;
        margin-bottom: 10px;
      }
      .wq-picker-tab {
        padding: 6px 12px;
        border-radius: 999px;
        background: transparent;
        border: 0;
        font: inherit; font-weight: 800; font-size: 13px;
        color: var(--ink-3);
        cursor: pointer;
      }
      .wq-picker-tab:hover:not(:disabled) { color: var(--ink); }
      .wq-picker-tab.active {
        background: var(--bg-warm);
        color: var(--primary-ink);
      }
      .wq-picker-tab:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
      .wq-picker-close {
        width: 26px; height: 26px;
        border-radius: 50%;
        border: 1.5px solid var(--line);
        background: var(--surface);
        color: var(--ink-2);
        cursor: pointer;
        font: inherit; font-weight: 900; font-size: 14px;
        line-height: 1;
      }
      .wq-frames-hint {
        font-size: 12px; font-weight: 600;
        color: var(--ink-2);
        margin-bottom: 8px;
      }
      .wq-frames-row {
        display: flex; gap: 8px;
        overflow-x: auto;
        padding-bottom: 4px;
      }
      .wq-frame-tile {
        position: relative;
        width: 124px; height: 78px;
        border-radius: 10px;
        border: 1.5px solid var(--line);
        background: var(--bg-warm);
        cursor: pointer;
        padding: 0;
        flex: 0 0 auto;
        overflow: hidden;
        transition: border-color 120ms ease, transform 120ms ease;
      }
      .wq-frame-tile:hover:not(:disabled) {
        border-color: var(--primary);
        transform: translateY(-2px);
      }
      .wq-frame-tile.loading {
        animation: wq-pulse 1.2s ease-in-out infinite;
        cursor: progress;
      }
      .wq-frame-tile.saving {
        opacity: 0.5;
        cursor: progress;
      }
      .wq-frame-tile img {
        width: 100%; height: 100%; object-fit: cover;
        display: block;
      }
      .wq-frame-t {
        position: absolute; bottom: 4px; right: 4px;
        font-size: 10px; font-weight: 800;
        background: rgba(0,0,0,0.6);
        color: #fff;
        padding: 2px 5px; border-radius: 4px;
      }
      @keyframes wq-pulse {
        0%,100% { background: var(--bg-warm); }
        50%     { background: #FFD6B0; }
      }
      .wq-frames-empty {
        padding: 12px;
        text-align: center;
        color: var(--ink-3);
        font-size: 13px; font-weight: 700;
      }

      .wq-upload-row {
        display: grid;
        grid-template-columns: 120px 1fr;
        gap: 12px;
        align-items: center;
      }
      .wq-upload-preview {
        aspect-ratio: 1 / 1;
        border-radius: 10px;
        overflow: hidden;
        background: var(--bg-warm);
        border: 1.5px dashed var(--line-strong);
        cursor: pointer;
        padding: 0;
        display: grid; place-items: center;
      }
      .wq-upload-preview img {
        width: 100%; height: 100%; object-fit: cover;
      }
      .wq-upload-empty {
        display: flex; flex-direction: column;
        align-items: center; gap: 4px;
        color: var(--ink-3);
        font-size: 12px; font-weight: 700;
      }
      .wq-upload-form {
        display: flex; flex-direction: column; gap: 8px;
      }
      .wq-upload-actions {
        display: flex; gap: 8px;
      }

      /* ── Time slider ─────────────────────────────────────────────────── */
      .wq-time { display: flex; flex-direction: column; gap: 8px; }
      .wq-time-row {
        display: flex; align-items: center; gap: 10px;
      }
      .wq-clock {
        width: 28px; height: 28px;
        border-radius: 50%;
        background: var(--surface);
        border: 1.5px solid var(--line);
        display: grid; place-items: center;
        color: var(--ink-2);
        flex: 0 0 auto;
      }
      .wq-slider {
        flex: 1;
        appearance: none;
        -webkit-appearance: none;
        height: 6px;
        border-radius: 999px;
        background: linear-gradient(
          to right,
          var(--primary) 0%,
          var(--primary) var(--p, 0%),
          var(--ink) var(--p, 0%),
          var(--ink) 100%
        );
        outline: none;
      }
      .wq-slider::-webkit-slider-thumb {
        appearance: none;
        -webkit-appearance: none;
        width: 18px; height: 18px;
        border-radius: 50%;
        background: var(--primary);
        border: 3px solid var(--surface);
        cursor: pointer;
        box-shadow: 0 2px 6px rgba(0,0,0,0.18);
      }
      .wq-slider::-moz-range-thumb {
        width: 18px; height: 18px;
        border-radius: 50%;
        background: var(--primary);
        border: 3px solid var(--surface);
        cursor: pointer;
      }
      .wq-time-readout {
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 14px; font-weight: 800;
        color: var(--ink);
        background: var(--surface);
        border: 1.5px solid var(--line);
        border-radius: 10px;
        padding: 6px 10px;
        flex: 0 0 auto;
        min-width: 56px;
        text-align: center;
      }

      .wq-error-bar {
        font-size: 12.5px; font-weight: 700;
        color: #C0392B;
        background: rgba(192,57,43,0.08);
        padding: 8px 10px;
        border-radius: 8px;
      }
      .wq-form-actions {
        display: flex; justify-content: flex-end; gap: 8px;
      }
    `}</style>
  );
}

export default WebQuestionEditor;
